import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, AdminGrade } from '../api';
import { Layout, Loading, ErrorBox } from '../components/Layout';
import {
  EntitlementPicker,
  PickerValue,
  emptyPicker,
  entitlementsFromPicker,
} from '../components/EntitlementPicker';

const field = 'w-full rounded-md border border-slate-300 px-3 py-2 text-sm';
const label = 'block text-sm font-medium text-slate-700 mb-1';

export function AdminSchoolNew() {
  const nav = useNavigate();
  const [grades, setGrades] = useState<AdminGrade[] | null>(null);
  const [picker, setPicker] = useState<PickerValue>(emptyPicker);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void api.admin
      .grades()
      .then(setGrades)
      .catch((e: Error) => setError(e.message));
  }, []);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    const f = new FormData(e.currentTarget);
    const str = (k: string) => (f.get(k) as string).trim();

    try {
      const school = await api.admin.createSchool({
        name: str('name'),
        contactName: str('contactName') || undefined,
        phone: str('phone') || undefined,
        email: str('email') || undefined,
        seatLimit: str('seatLimit') ? Number(str('seatLimit')) : undefined,
        // The input is a date; the API takes ISO-8601. End-of-day so a licence
        // dated today is still valid for the whole of today.
        licenseEnd: str('licenseEnd')
          ? new Date(`${str('licenseEnd')}T23:59:59Z`).toISOString()
          : undefined,
        entitlements: entitlementsFromPicker(picker),
        admin: str('adminEmail')
          ? { email: str('adminEmail'), password: str('adminPassword') }
          : undefined,
      });
      nav(`/admin/schools/${school.id}`);
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6">
        <Link to="/admin/schools" className="text-sm text-slate-500 hover:text-slate-900">
          ← Schools
        </Link>
        <h1 className="text-xl font-semibold text-slate-900 mt-1">New school</h1>
      </div>

      {error && (
        <div className="mb-4">
          <ErrorBox message={error} />
        </div>
      )}

      <form onSubmit={(e) => void submit(e)} className="space-y-6">
        <section className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={label} htmlFor="name">
                School name
              </label>
              <input id="name" name="name" required maxLength={200} className={field} />
            </div>
            <div>
              <label className={label} htmlFor="contactName">
                Contact name
              </label>
              <input id="contactName" name="contactName" maxLength={200} className={field} />
            </div>
            <div>
              <label className={label} htmlFor="phone">
                Phone
              </label>
              <input id="phone" name="phone" maxLength={50} className={field} />
            </div>
            <div>
              <label className={label} htmlFor="email">
                Contact email
              </label>
              <input id="email" name="email" type="email" maxLength={254} className={field} />
            </div>
            <div>
              <label className={label} htmlFor="seatLimit">
                Seat limit
              </label>
              <input
                id="seatLimit"
                name="seatLimit"
                type="number"
                min={1}
                defaultValue={50}
                className={field}
              />
            </div>
            <div>
              <label className={label} htmlFor="licenseEnd">
                Licence ends
              </label>
              <input id="licenseEnd" name="licenseEnd" type="date" className={field} />
              <p className="text-xs text-slate-500 mt-1">Leave blank for no expiry.</p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-sm font-semibold text-slate-900 mb-1">First school admin</h2>
          <p className="text-xs text-slate-500 mb-3">
            Created with the school. This is the account the school signs in with.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={label} htmlFor="adminEmail">
                Email
              </label>
              <input id="adminEmail" name="adminEmail" type="email" className={field} />
            </div>
            <div>
              <label className={label} htmlFor="adminPassword">
                Password
              </label>
              <input
                id="adminPassword"
                name="adminPassword"
                type="password"
                minLength={8}
                className={field}
              />
              <p className="text-xs text-slate-500 mt-1">At least 8 characters.</p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-sm font-semibold text-slate-900 mb-1">Entitlements</h2>
          <p className="text-xs text-slate-500 mb-3">
            What this school may see. A grade left at “No access” is hidden entirely.
          </p>
          {!grades && !error && <Loading />}
          {grades && (
            <EntitlementPicker grades={grades} value={picker} onChange={setPicker} />
          )}
        </section>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? 'Creating…' : 'Create school'}
          </button>
          <Link
            to="/admin/schools"
            className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </Link>
        </div>
      </form>
    </Layout>
  );
}
